import React, { useState, useEffect } from 'react';
import { 
  Clock, AlertTriangle, AlertCircle, CheckCircle, 
  ChevronRight, FileText, Calendar, DollarSign,
  TrendingUp, Users, Mail, Phone
} from 'lucide-react';
import api from '../../services/api';
import { formatCurrency } from '../../utils/helpers';
import Loading from '../Loading';

const InvoiceAging = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedBucket, setExpandedBucket] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/reports/invoice-aging');
      setData(response.data);
    } catch (err) {
      console.error('Error loading invoice aging:', err);
      setError('Failed to load invoice data');
    } finally {
      setLoading(false);
    }
  };

  const getBucketConfig = (bucket) => {
    const configs = {
      'current': {
        label: 'Current',
        sublabel: '0-30 days',
        color: 'green',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        textColor: 'text-green-700',
        iconColor: 'text-green-500',
        icon: CheckCircle,
        barColor: 'bg-green-500',
      },
      '31-60': {
        label: 'Overdue',
        sublabel: '31-60 days',
        color: 'yellow',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        textColor: 'text-yellow-700',
        iconColor: 'text-yellow-500',
        icon: Clock,
        barColor: 'bg-yellow-500',
      },
      '61-90': {
        label: 'Seriously Overdue',
        sublabel: '61-90 days',
        color: 'orange',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        textColor: 'text-orange-700',
        iconColor: 'text-orange-500',
        icon: AlertTriangle,
        barColor: 'bg-orange-500',
      },
      '90+': {
        label: 'Critical',
        sublabel: '90+ days',
        color: 'red',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        textColor: 'text-red-700',
        iconColor: 'text-red-500',
        icon: AlertCircle,
        barColor: 'bg-red-500',
      },
    };
    return configs[bucket] || configs['current'];
  };

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-800">{error}</p>
        <button 
          onClick={loadData}
          className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const {
    totalOutstanding = 0,
    totalInvoices = 0,
    buckets = [],
    topDebtors = [],
    customerCount = 0,
    averageDaysOutstanding = 0,
    oldestInvoiceDays = 0,
  } = data || {};

  // Calculate max for visual bars
  const maxBucketAmount = Math.max(...buckets.map(b => b.amount), 1);

  // All caught up state
  if (totalOutstanding === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-green-900 mb-2">All Caught Up! 🎉</h3>
        <p className="text-green-700">
          You have no outstanding invoices. Great job staying on top of collections!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Total Outstanding */}
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between mb-1">
            <span className="text-amber-100 text-xs font-medium">Outstanding</span>
            <DollarSign size={16} className="text-amber-200" />
          </div>
          <p className="text-2xl font-bold">{formatCurrency(totalOutstanding)}</p>
          <p className="text-xs text-amber-100 mt-1">
            {totalInvoices} invoice{totalInvoices !== 1 ? 's' : ''} unpaid
          </p>
        </div>

        {/* Avg Days Outstanding */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-blue-700 text-xs font-medium">Avg Age</span>
            <Calendar size={16} className="text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-blue-900">{averageDaysOutstanding}d</p>
          <p className="text-xs text-blue-600 mt-1">
            average days outstanding
          </p>
        </div>

        {/* Oldest Invoice */}
        <div className={`rounded-xl p-4 ${oldestInvoiceDays > 60 ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`}>
          <div className="flex items-center justify-between mb-1">
            <span className={`text-xs font-medium ${oldestInvoiceDays > 60 ? 'text-red-700' : 'text-gray-500'}`}>
              Oldest
            </span>
            <Clock size={16} className={oldestInvoiceDays > 60 ? 'text-red-500' : 'text-gray-400'} />
          </div>
          <p className={`text-2xl font-bold ${oldestInvoiceDays > 60 ? 'text-red-900' : 'text-gray-900'}`}>
            {oldestInvoiceDays}d
          </p>
          <p className={`text-xs mt-1 ${oldestInvoiceDays > 60 ? 'text-red-600' : 'text-gray-500'}`}>
            oldest unpaid invoice
          </p>
        </div>

        {/* Customers with Balance */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-purple-700 text-xs font-medium">Customers</span>
            <Users size={16} className="text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-purple-900">{customerCount}</p>
          <p className="text-xs text-purple-600 mt-1">
            with outstanding balance
          </p>
        </div>
      </div>

      {/* Aging Buckets */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Aging Breakdown</h3>
        
        <div className="space-y-3">
          {buckets.map((bucket) => {
            const config = getBucketConfig(bucket.bucket);
            const Icon = config.icon;
            const barWidth = (bucket.amount / maxBucketAmount * 100).toFixed(0);
            const isExpanded = expandedBucket === bucket.bucket;
            const percent = totalOutstanding > 0 
              ? (bucket.amount / totalOutstanding * 100).toFixed(0) 
              : 0;
            
            return (
              <div key={bucket.bucket}>
                <button
                  onClick={() => setExpandedBucket(isExpanded ? null : bucket.bucket)}
                  className={`w-full text-left rounded-lg p-3 transition-colors ${config.bgColor} ${config.borderColor} border`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon size={18} className={config.iconColor} />
                      <div>
                        <span className={`text-sm font-semibold ${config.textColor}`}>
                          {config.label}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          {config.sublabel}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <span className={`text-sm font-bold ${config.textColor}`}>
                          {formatCurrency(bucket.amount)}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">
                          ({bucket.count})
                        </span>
                      </div>
                      <ChevronRight 
                        size={16} 
                        className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                      />
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="h-2 bg-white/50 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${config.barColor} rounded-full transition-all duration-500`}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1 text-right">{percent}% of total</p>
                </button>

                {/* Expanded invoice list */}
                {isExpanded && bucket.invoices?.length > 0 && (
                  <div className="mt-2 ml-4 space-y-2">
                    {bucket.invoices.map((invoice) => (
                      <div 
                        key={invoice.id}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {invoice.customerName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {invoice.invoiceNumber} • {invoice.daysOld} days past due
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">
                            {formatCurrency(invoice.amountDue)}
                          </span>
                          <a
                            href={`/invoices?id=${invoice.id}`}
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            title="View invoice"
                          >
                            <FileText size={16} />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Debtors */}
      {topDebtors.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Customers with Balances</h3>
          </div>
          
          <div className="space-y-2">
            {topDebtors.slice(0, 5).map((debtor, index) => (
              <div 
                key={debtor.customerId || index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {debtor.customerName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {debtor.invoiceCount} invoice{debtor.invoiceCount !== 1 ? 's' : ''} • 
                    oldest {debtor.oldestDays} days
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-bold ${debtor.oldestDays > 60 ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatCurrency(debtor.totalOwed)}
                  </span>
                  <div className="flex items-center gap-1">
                    {debtor.email && (
                      <a
                        href={`mailto:${debtor.email}?subject=Outstanding Balance Reminder`}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Send email"
                      >
                        <Mail size={16} />
                      </a>
                    )}
                    {debtor.phone && (
                      <a
                        href={`tel:${debtor.phone}`}
                        className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Call customer"
                      >
                        <Phone size={16} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {topDebtors.length > 5 && (
            <a 
              href="/invoices?status=unpaid"
              className="block text-center text-sm text-primary-600 hover:text-primary-700 mt-3 py-2"
            >
              View all {topDebtors.length} customers →
            </a>
          )}
        </div>
      )}

      {/* Action Tips */}
      {buckets.some(b => b.bucket === '61-90' || b.bucket === '90+') && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h4 className="font-semibold text-amber-900">Collection Tips</h4>
              <ul className="text-sm text-amber-700 mt-1 space-y-1">
                <li>• Send friendly reminders at 30 days</li>
                <li>• Make a phone call at 60 days</li>
                <li>• Consider payment plans for large balances</li>
                <li>• Stop work for customers 90+ days overdue</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceAging;